const logging = require('@sstdev/lib_logging');
const dataAccessMethods = require('../../../dataAccessMethods');
const Blockly = require('blockly');
const baseMdcBlockGroup = require('../_BaseMdcBlockGroup');
const { config } = require('@sstdev/lib_metadata-config');
const setupAddCodeProperties = require('../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../helpers/convertToBoolean');
const { prettyName } = require('../../../helpers/blockInitMethods');
const TOOLTIPS = {
    orderBy: 'Sort dataset by a single column. For sorting on multiple columns, add multiple OrderBy blocks.'
};
const hNodeTypes = require('./types');

class Filter extends baseMdcBlockGroup {
    /**
     * @param {{[key: string]: any}} code
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addCodeGeneratorCustom(code, blocklyBlock, hNodeType) {
        if (hNodeTypes[hNodeType]?.convertCustomFieldsToCode) {
            const resultCode = hNodeTypes[hNodeType].convertCustomFieldsToCode(code, blocklyBlock);
            if (typeof resultCode === 'string') {
                logging.error(
                    `${hNodeType}.convertCustomFieldsToCode should not return a string. It should return a JSON object instead.`
                );
                return resultCode;
            }
            return JSON.stringify(resultCode);
        }
        const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);

        const hNodesThatDontNeedNamespaceOrRelation = [
            'AggregateType',
            'AlsoSearchWithAscii',
            'ExactMatchOnly',
            'OrUndefined',
            'Paged',
            'PropertiesToSearch'
        ];
        const hNodesThatNeedNamespaceRelationButDontNeedPropertyName = ['FullTextSearch'];

        if (!hNodesThatDontNeedNamespaceOrRelation.includes(hNodeType)) {
            addCodeProperties(['namespace', 'relation']);

            if (!hNodesThatNeedNamespaceRelationButDontNeedPropertyName.includes(hNodeType)) {
                addCodeProperties(['propertyName']);
            }
        }

        switch (hNodeType) {
            // These need no additional codeProperties
            case 'ExactMatchOnly': // This is just a boolean - so no need for properties
            case 'DefaultToPreviousSelection':
            case 'IncludeDeleted':
            case 'GroupBy':
            case 'OrUndefined':
            case 'ValueExists':
            case 'ValueDoesNotExist':
                break;
            case 'AlsoSearchWithAscii':
                addCodeProperties([{ key: 'alsoSearchWithAscii', handler: val => val.split(',') }]);
                break;
            case 'DateRange':
                addCodeProperties([
                    'beginDateOffsetUnit',
                    'beginDateOffsetInterval',
                    'endDateOffsetUnit',
                    'endDateOffsetInterval'
                ]);
                break;
            case 'DefaultValue':
                addCodeProperties(['defaultSelectedValue', 'propertyName']);
                break;
            case 'FullTextSearch':
                addCodeProperties(['changeAction']);
                break;
            case 'Paged':
                // 'page size' makes sense to a MDC user, but 'limit' is what this is called
                // in the actual code.
                code.limit = Number.parseInt(blocklyBlock.getFieldValue('pageSize'));
                break;
            case 'OrderBy':
                addCodeProperties([{ key: 'direction', value: 'orderDirection' }]);
                break;
            case 'GroupByBoolean':
                addCodeProperties(['trueLabel', 'falseLabel']);
                break;
            case 'ExcludedValues':
                addCodeProperties([{ key: 'allowUndefined', handler: convertToBoolean }]);
                addCodeProperties([{ key: 'values', handler: val => val.split(',') }]);
                break;
            case 'IncludedValues':
                addCodeProperties([{ key: 'values', handler: val => val.split(',') }]);
                break;
            case 'PropertiesToSearch':
                addCodeProperties([{ key: 'propertiesToSearch', handler: val => val.split(',') }]);
                break;
            case 'AggregateType':
                addCodeProperties(['aggregateType']);
                break;
            default:
                throw new Error(`Unknown filter type: ${hNodeType}`);
        }

        return JSON.stringify(code);
    }

    /**
     * @param {unknown} color
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addToBlocksCustom(color, blocklyBlock, hNodeType, mdcBlock, topInput) {
        if (hNodeTypes[hNodeType]) {
            hNodeTypes[hNodeType].addBlockSpecificFields(color, blocklyBlock, mdcBlock, topInput);
            return;
        }
        const blockId = blocklyBlock.id;
        if (TOOLTIPS[hNodeType]) {
            blocklyBlock.setTooltip(TOOLTIPS[hNodeType]);
        }
        const beginDateUnitDropDown = new Blockly.FieldDropdown(config.dateOffsetUnit);
        beginDateUnitDropDown.setValue('months');
        const endDateUnitDropDown = new Blockly.FieldDropdown(config.dateOffsetUnit);
        endDateUnitDropDown.setValue('days');
        const workspace = blocklyBlock.workspace;

        switch (hNodeType) {
            case 'DateRange':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Namespace:')
                    .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Relation:')
                    .appendField(
                        new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                        'relation'
                    );
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Property Name:')
                    .appendField(new Blockly.FieldTextInput('meta.createdTime'), 'propertyName');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('Begin date offset:')
                    .appendField(beginDateUnitDropDown, 'beginDateOffsetUnit')
                    .appendField(new Blockly.FieldNumber(-1, -99, 99, 1), 'beginDateOffsetInterval');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('End date offset:')
                    .appendField(endDateUnitDropDown, 'endDateOffsetUnit')
                    .appendField(new Blockly.FieldNumber(0, -99, 99, 1), 'endDateOffsetInterval');
                break;
            case 'DefaultValue':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Property Name:')
                    .appendField(new Blockly.FieldTextInput('title'), 'propertyName');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Default Selected Value:')
                    .appendField(new Blockly.FieldTextInput(), 'defaultSelectedValue');
                break;
            case 'ExactMatchOnly':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('Use Exact Match When Filtering Text');
                break;
            case 'IncludeDeleted':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('Include deleted records in results');
                break;
            case 'DefaultToPreviousSelection':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('Default value to previous selection');
                break;
            case 'OrUndefined':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('Also match records where the value is undefined');
                blocklyBlock.setTooltip(
                    'Expands the filter to also include records where the property value is undefined or missing.'
                );
                break;
            case 'FullTextSearch':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Namespace:')
                    .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Relation:')
                    .appendField(
                        new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                        'relation'
                    );
                break;
            case 'Paged':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Page Size:')
                    .appendField(new Blockly.FieldNumber(25, 1, undefined, 0), 'pageSize');
                break;
            case 'OrderBy':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Namespace:')
                    .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getNamespaces(true)), 'namespace');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Relation:')
                    .appendField(
                        new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId, true)),
                        'relation'
                    );
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Property Name:')
                    .appendField(new Blockly.FieldTextInput('title'), 'propertyName');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('Direction:')
                    .appendField(
                        new Blockly.FieldDropdown([
                            ['ascending', 'ascending'],
                            ['descending', 'descending']
                        ]),
                        'orderDirection'
                    );
                break;
            case 'GroupBy':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Namespace:')
                    .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getNamespaces(true)), 'namespace');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Relation:')
                    .appendField(
                        new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId, true)),
                        'relation'
                    );
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Property Name:')
                    .appendField(new Blockly.FieldTextInput(''), 'propertyName');
                break;
            case 'GroupByBoolean':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Namespace:')
                    .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getNamespaces(true)), 'namespace');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Relation:')
                    .appendField(
                        new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId, true)),
                        'relation'
                    );
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Property Name:')
                    .appendField(new Blockly.FieldTextInput(''), 'propertyName');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  True Label:')
                    .appendField(new Blockly.FieldTextInput(''), 'trueLabel');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  False Label:')
                    .appendField(new Blockly.FieldTextInput(''), 'falseLabel');
                break;
            case 'ExcludedValues':
            case 'IncludedValues':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Namespace:')
                    .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getNamespaces(true)), 'namespace');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Relation:')
                    .appendField(
                        new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId, true)),
                        'relation'
                    );
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Property Name:')
                    .appendField(new Blockly.FieldTextInput(''), 'propertyName');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Values:')
                    .appendField(new Blockly.FieldTextInput('', validateCommaSeparatedList), 'values');
                blocklyBlock.setTooltip(
                    'A comma separated list of values to exclude for the property name (no spaces allowed).'
                );
                if (hNodeType === 'ExcludedValues') {
                    blocklyBlock
                        .appendDummyInput()
                        .appendField("  Don't exclude `undefined`:")
                        .appendField(new Blockly.FieldCheckbox(false), 'allowUndefined');
                }
                break;
            case 'ValueExists':
            case 'ValueDoesNotExist':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Namespace:')
                    .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getNamespaces(true)), 'namespace');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Optional Relation:')
                    .appendField(
                        new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId, true)),
                        'relation'
                    );
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  PropertyName:')
                    .appendField(new Blockly.FieldTextInput(), 'propertyName');
                blocklyBlock.setTooltip(
                    `Filters records where the value for the propertyName does${
                        hNodeType === 'ValueExists' ? '' : ' not'
                    } exist`
                );
                break;
            case 'PropertiesToSearch':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Properties to Search:')
                    .appendField(new Blockly.FieldTextInput(''), 'propertiesToSearch');
                blocklyBlock.setTooltip(
                    'A comma separated list of values to search when doing a full text search (no spaces allowed).'
                );
                break;
            case 'AlsoSearchWithAscii':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Also Search Ascii Text:')
                    .appendField(new Blockly.FieldTextInput('tagId'), 'alsoSearchWithAscii');
                blocklyBlock.setTooltip(
                    'A comma separated list of fields to convert to ascii and include in full text search (no spaces allowed).'
                );
                break;
            case 'AggregateType':
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Aggregate Type:')
                    .appendField(new Blockly.FieldDropdown([['count', 'count']]), 'aggregateType');
                break;
            default:
                throw new Error(`Unknown filter type: ${hNodeType}`);
        }
    }

    // Override the default block name for something easier to understand
    getBlockName(hNodeType) {
        switch (hNodeType) {
            case 'ForeignRelationSelectionOnForm':
                return 'By Parent Selection';
            case 'ForeignRelationActiveRecord':
                return 'By Grid Selection';
            case 'ForeignRelationSplashRecord':
                return 'By Splash Screen Selection';
            case 'NestedListEntry':
                return 'Where A Single Nested List Entry in';
            default:
                return prettyName(hNodeType);
        }
    }
}

module.exports = Filter;
/**
 * If the input matches return the input otherwise return null
 * @param {string} text This should be a comma separated list with no spaces after the commas
 */
function validateCommaSeparatedList(text) {
    if (text === '') return text;
    const match = /^[- \w]+(?:,[^ ][- \w]*)*$/.exec(text);
    if (match && match.length > 0) {
        return text;
    }
    return null;
}
